import type { ErrorEvent, EventHint } from "@sentry/react"

/**
 * Client-side Sentry severity tuning for expected backend callable rejections.
 *
 * Per ADR-0020 the backend records auth/validation `HttpsError` throws at WARN
 * (not ERROR) so single-user typos stay quiet while a spike of identical throws
 * — the fingerprint of broken frontend/backend wiring — remains visible. The
 * web app captures the *same* throws independently (the Firebase JS SDK surfaces
 * them as a `FirebaseError` with a namespaced `functions/…` code), so without
 * tuning they re-appear here as client-side ERRORs and re-introduce the noise
 * the backend already suppressed (PLA-178, e.g. CARASSIST-WEB-18).
 *
 * This module downgrades — never drops — those events to WARN, mirroring the
 * backend's WARN_CODES set in `functions/src/config/sentry-severity.ts`.
 */

/**
 * Namespaced Firebase callable error codes (`FirebaseError.code`) that represent
 * expected auth/validation outcomes. Mirrors WARN_CODES on the backend.
 */
export const EXPECTED_FUNCTION_ERROR_CODES: ReadonlySet<string> = new Set([
  "functions/unauthenticated",
  "functions/permission-denied",
  "functions/failed-precondition",
  "functions/invalid-argument",
  "functions/out-of-range",
  "functions/not-found",
  "functions/already-exists",
])

/**
 * Message substrings for expected throws — the workhorse client-side matcher.
 *
 * Clients routinely rewrap the callable `FirebaseError` into a plain `Error` for
 * display, or log it via `console.error`, which strips the `functions/…` code
 * before the event reaches Sentry (e.g. CARASSIST-WEB-18 arrives as a plain Error
 * with only `.message`; LIRIA-QCAI-INSPECT-Y as a rewrapped admin-guard Error).
 * The code match above is therefore best-effort; these distinctive backend
 * validation/auth message substrings are what actually catch the listed issues.
 * Each is specific enough not to match unrelated errors. See ADR-0020 / PLA-178.
 */
export const EXPECTED_MESSAGE_SUBSTRINGS: readonly string[] = [
  "Buchung fehlgeschlagen", // generic B2C fallback (functions/internal) — HDP-BOOKING-MODAL-WIDGET-4
  "estimatedLegMinutes", // booking leg-time range check (invalid-argument) — CARASSIST-WEB-18
  "Standort geschlossen", // closed-day (failed-precondition) — BACKEND-FIREBASE-J
  "An diesem Tag geschlossen", // holiday closed-day variant — BACKEND-FIREBASE-V
  "Buchungen sind nur von", // outside opening hours (failed-precondition) — BACKEND-FIREBASE-R
  "nur für Administratoren", // admin-only guard (permission-denied) — LIRIA-QCAI-INSPECT-Y
  "Login erforderlich", // auth-required guard (unauthenticated) — BACKEND-FIREBASE-1C
  "User must be authenticated", // auth-required guard (unauthenticated) — LIRIA-QCAI-INSPECT-1E/15/1A
  // requireB2bCustomer guard (permission-denied) — CARASSIST-WEB-2T / -V, PLA-534.
  // rewrapCallableError now preserves the code so the match above normally wins;
  // these remain the safety net for any caller that still rewraps bare.
  "Kein gültiges B2B-Kundenkonto",
  "Kein Kundenkonto verknüpft",
]

function functionErrorCode(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null | undefined)?.code
  return typeof code === "string" ? code : undefined
}

function collectMessages(event: ErrorEvent, hint: EventHint | undefined): string[] {
  const messages: string[] = []
  const original = hint?.originalException as { message?: unknown } | undefined
  if (typeof original?.message === "string") messages.push(original.message)
  for (const value of event.exception?.values ?? []) {
    if (typeof value?.value === "string") messages.push(value.value)
  }
  if (typeof event.message === "string") messages.push(event.message)
  return messages
}

/**
 * True when an event represents an expected callable rejection the backend
 * already records at WARN — matched first by Firebase functions error code,
 * then by known message substring as a fallback.
 */
export function isExpectedThrow(event: ErrorEvent, hint?: EventHint): boolean {
  const code = functionErrorCode(hint?.originalException)
  if (code !== undefined && EXPECTED_FUNCTION_ERROR_CODES.has(code)) return true

  const messages = collectMessages(event, hint)
  return messages.some((message) =>
    EXPECTED_MESSAGE_SUBSTRINGS.some((substring) => message.includes(substring)),
  )
}

/**
 * Sentry `beforeSend` hook: downgrade (never drop) expected callable rejections
 * to WARN so spike-detection still works. See PLA-178 / ADR-0020.
 */
export function downgradeExpectedThrow(event: ErrorEvent, hint?: EventHint): ErrorEvent {
  if (isExpectedThrow(event, hint)) {
    event.level = "warning"
    const code = functionErrorCode(hint?.originalException)
    event.tags = {
      ...event.tags,
      expectedValidationThrow: "true",
      ...(code ? { httpsErrorCode: code } : {}),
    }
  }
  return event
}
